import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

// User interface
interface UserProfile {
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: string;
    city?: string;
    zipCode?: string;
    country?: string;
    nationality?: string;
    gender?: 'male' | 'female' | '';
    birthDay?: string;
    birthMonth?: string;
    birthYear?: string;
    tcId?: string;
    notTcCitizen?: boolean;
    countryCode?: string;
    passports?: { number: string; country: string; expiry: string }[];
    mileCards?: { airline: string; number: string }[];
    marketingConsent?: boolean;
}

interface User {
    _id: string;
    email: string;
    role: 'admin' | 'user';
    provider: 'local' | 'google';
    avatarUrl?: string;
    profile: UserProfile;
    isActive: boolean;
    emailVerified: boolean;
    createdAt: string;
    bookings?: any[];
    savedHotels?: { hotelId: string; name: string; savedAt: string }[];
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isLoading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<boolean>;
    register: (data: RegisterData) => Promise<boolean>;
    logout: () => Promise<void>;
    updateProfile: (profile: Partial<UserProfile>) => Promise<boolean>;
    refreshUser: () => Promise<void>;
    clearError: () => void;
}

interface RegisterData {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// API base
const API_BASE = '/api/auth';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchingRef = React.useRef(false);

    // Fetch current user on mount
    const refreshUser = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;

        try {
            const response = await fetch(`${API_BASE}/me`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.user) {
                    setUser(data.user);
                }
            } else {
                setUser(null);
            }
        } catch (err) {
            console.warn('Auth check failed:', err);
            setUser(null);
        } finally {
            fetchingRef.current = false;
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshUser();

        // Check for login success from OAuth redirect
        const params = new URLSearchParams(window.location.search);
        if (params.get('login') === 'success') {
            refreshUser();
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [refreshUser]);

    const login = async (email: string, password: string): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email, password })
            });

            const data = await response.json();

            if (data.success && data.user) {
                setUser(data.user);
                return true;
            } else {
                setError(data.message || 'Login failed');
                return false;
            }
        } catch (err) {
            setError('Network error. Please try again.');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (regData: RegisterData): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE}/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(regData)
            });

            const data = await response.json();

            if (data.success && data.user) {
                setUser(data.user);
                return true;
            } else {
                setError(data.message || 'Registration failed');
                return false;
            }
        } catch (err) {
            setError('Network error. Please try again.');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async (): Promise<void> => {
        try {
            await fetch(`${API_BASE}/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (err) {
            console.warn('Logout error:', err);
        } finally {
            setUser(null);
        }
    };

    const updateProfile = async (profile: Partial<UserProfile>): Promise<boolean> => {
        try {
            const response = await fetch(`${API_BASE}/profile`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(profile)
            });

            const data = await response.json();

            if (data.success && data.user) {
                setUser(data.user);
                return true;
            }
            return false;
        } catch (err) {
            return false;
        }
    };

    const clearError = () => setError(null);

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isLoading,
        error,
        login,
        register,
        logout,
        updateProfile,
        refreshUser,
        clearError
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Google Login helper
export const initiateGoogleLogin = () => {
    window.location.href = '/api/auth/google';
};

export default AuthContext;
